import { TriggerEventProps } from '../../../../models';
import { EventStrategy } from './EventStrategy';

export type PageEvent = {
  href: string;
};

/**
 * The shape of the payload for the PageView event.
 * @public
 */
export interface PageEventProps extends TriggerEventProps {
  event: 'PageView';
  /**
   * A URL that can contain wildcards.
   * @remarks
   * For example, `https://samelogic.com/*\/docs` matches all URLs that contain `samelogic.com` and end with `/docs`.
   */
  urlPattern: string;
}

export class PageEventStrategy extends EventStrategy<PageEventProps, PageEvent> {
  private previousUrl = location.href;
  public start(): void {
    if (this.isConditionMet()) {
      this.handlePageView();
    }

    const observer = new MutationObserver(() => {
      if (this.previousUrl !== location.href) {
        this.previousUrl = location.href;
        if (this.isConditionMet()) {
          this.handlePageView();
        }
      }
    });
    observer.observe(document, { subtree: true, childList: true });
  }

  private handlePageView = () => {
    this.onEventTriggered?.({
      condition: true,
      props: this.props,
      data: { href: location.href }
    });
  };

  private isConditionMet(): boolean {
    const props = this.props;
    // wildcard match should only care about origin + path
    const url = location.origin + location.pathname;
    return this.isUrlMatch(url, props.urlPattern);
  }

  private isUrlMatch(str: string, rule: string): boolean {
    const escapeRegex = (str: string) => str.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
    // eslint-disable-next-line security/detect-non-literal-regexp
    return new RegExp('^' + rule.split('*').map(escapeRegex).join('.*') + '$').test(str);
  }
}
